import { useState, useEffect } from 'react';
import { Plus, X, Target, Filter, Users, Loader2, Eye } from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-frontend/components/ui/select';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { apiClient } from '@/lib/api';

interface TargetingCondition {
  field: string;
  operator: string;
  value: string;
}

interface TargetingRules {
  include: TargetingCondition[];
  exclude: TargetingCondition[];
}

interface Template {
  id: string;
  name: string;
  description: string;
}

interface TargetingPreview {
  total_leads: number;
  excluded_dnc: number;
  excluded_frequency: number;
  excluded_targeting: number;
  final_leads: number;
  breakdown: Record<string, number>;
}

interface TargetingRuleBuilderProps {
  campaignId?: string;
  initialRules?: TargetingRules | null;
  initialTemplateId?: string | null;
  onSave?: () => void;
  /** Controlled / local mode: if provided, all changes propagate up and the internal Save button is hidden. */
  onChange?: (rules: TargetingRules | null, templateId: string | null) => void;
  hideSaveButton?: boolean;
  hideTemplateSelect?: boolean;
}

const fieldOptions = [
  { value: 'tag', label: 'Tag' },
  { value: 'stage', label: 'Stage' },
  { value: 'source', label: 'Sorgente' },
  { value: 'assigned_to', label: 'Assegnato a' },
  { value: 'priority', label: 'Priorità' },
];

const operatorOptions = [
  { value: 'is', label: 'è' },
  { value: 'is_not', label: 'non è' },
  { value: 'contains', label: 'contiene' },
];

export default function TargetingRuleBuilder({ campaignId, initialRules, initialTemplateId, onSave, onChange, hideSaveButton, hideTemplateSelect }: TargetingRuleBuilderProps) {
  const [rules, setRules] = useState<TargetingRules>(initialRules || { include: [], exclude: [] });
  const [templateId, setTemplateId] = useState(initialTemplateId || '__none__');
  const [templates, setTemplates] = useState<Template[]>([]);
  const [preview, setPreview] = useState<TargetingPreview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const controlled = typeof onChange === 'function';

  const loadTemplates = async () => {
    try {
      const res = await apiClient.get<{ id: string; name: string; description: string }[]>('/crm/contactability-templates');
      if (res.success) setTemplates(res.data || []);
    } catch (err) {
      console.error('Error loading templates:', err);
    }
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  useEffect(() => {
    if (!controlled) return;
    const hasRules = rules.include.length > 0 || rules.exclude.length > 0;
    onChange(hasRules ? rules : null, templateId === '__none__' ? null : (templateId || null));
  }, [rules, templateId, controlled, onChange]);

  const addCondition = (type: 'include' | 'exclude') => {
    const newCond: TargetingCondition = { field: 'tag', operator: 'is', value: '' };
    setRules({ ...rules, [type]: [...rules[type], newCond] });
  };

  const removeCondition = (type: 'include' | 'exclude', idx: number) => {
    const arr = [...rules[type]];
    arr.splice(idx, 1);
    setRules({ ...rules, [type]: arr });
  };

  const updateCondition = (type: 'include' | 'exclude', idx: number, field: string, value: string) => {
    const arr = [...rules[type]];
    arr[idx] = { ...arr[idx]!, [field]: value };
    setRules({ ...rules, [type]: arr });
  };

  const loadPreview = async () => {
    if (!campaignId) return;
    setLoadingPreview(true);
    try {
      const res = await apiClient.get<{ total_leads: number; excluded_dnc: number; excluded_frequency: number; excluded_targeting: number; final_leads: number; breakdown: Record<string, number> }>(`/crm/campaigns/${campaignId}/targeting-preview?channel=voice`);
      if (res.success) setPreview(res.data ?? null);
    } catch (err) {
      console.error('Error loading preview:', err);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.put(`/crm/campaigns/${campaignId}/targeting-rules`, {
        targeting_rules: rules.include.length > 0 || rules.exclude.length > 0 ? rules : null,
        contactability_template_id: templateId === '__none__' ? null : templateId || null,
      });
      onSave?.();
    } catch (err) {
      console.error('Error saving targeting:', err);
    } finally {
      setSaving(false);
    }
  };

  const renderConditions = (type: 'include' | 'exclude', label: string, color: string) => (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-xs font-medium flex items-center gap-1">
          <Filter className="h-3 w-3" /> {label}
        </Label>
        <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => addCondition(type)}>
          <Plus className="h-3 w-3 mr-1" /> Aggiungi
        </Button>
      </div>
      {rules[type].map((cond, idx) => (
        <div key={idx} className={`flex items-center gap-2 p-2 rounded border ${color}`}>
          <Select value={cond.field} onValueChange={v => updateCondition(type, idx, 'field', v)}>
            <SelectTrigger className="w-[110px] h-7 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              {fieldOptions.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={cond.operator} onValueChange={v => updateCondition(type, idx, 'operator', v)}>
            <SelectTrigger className="w-[90px] h-7 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              {operatorOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input
            className="flex-1 h-7 text-xs"
            placeholder="Valore"
            value={cond.value}
            onChange={e => updateCondition(type, idx, 'value', e.target.value)}
          />
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => removeCondition(type, idx)}>
            <X className="h-3 w-3 text-gray-400" />
          </Button>
        </div>
      ))}
      {rules[type].length === 0 && (
        <p className="text-[10px] text-gray-400 italic pl-1">Nessuna regola {type === 'include' ? 'di inclusione' : 'di esclusione'}</p>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Target className="h-4 w-4 text-blue-500" />
        <span className="font-medium text-sm">Targeting Unificato</span>
      </div>

      {!hideTemplateSelect && (
        <div>
          <Label className="text-xs">Template Contattabilità</Label>
          <Select value={templateId} onValueChange={setTemplateId}>
            <SelectTrigger className="h-8 text-xs mt-1"><SelectValue placeholder="Nessun template (libero)" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Nessun template</SelectItem>
              {templates.map(t => (
                <SelectItem key={t.id} value={t.id}>{t.name}{t.description ? ` - ${t.description}` : ''}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {renderConditions('include', 'Regole di Inclusione', 'border-green-100 bg-green-50/50')}
      {renderConditions('exclude', 'Regole di Esclusione', 'border-red-100 bg-red-50/50')}

      <div className="flex items-center gap-2">
        {campaignId && (
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={loadPreview} disabled={loadingPreview}>
            {loadingPreview ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Eye className="h-3 w-3 mr-1" />}
            Anteprima
          </Button>
        )}
        {!hideSaveButton && !controlled && campaignId && (
          <Button size="sm" className="h-8 text-xs" onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
            Salva Regole
          </Button>
        )}
      </div>

      {preview && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 p-3 bg-gray-50 rounded-lg border">
          <div className="text-center">
            <div className="text-lg font-bold text-gray-700">{preview.total_leads}</div>
            <div className="text-[10px] text-gray-500">Lead Totali</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-red-600">{preview.excluded_dnc}</div>
            <div className="text-[10px] text-gray-500">Esclusi DNC</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-amber-600">{preview.excluded_frequency}</div>
            <div className="text-[10px] text-gray-500">Esclusi Frequenza</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-orange-600">{preview.excluded_targeting}</div>
            <div className="text-[10px] text-gray-500">Esclusi Targeting</div>
          </div>
          <div className="text-center col-span-2 sm:col-span-2">
            <div className="text-2xl font-bold text-green-600">{preview.final_leads}</div>
            <div className="text-[10px] text-gray-500 flex items-center justify-center gap-1">
              <Users className="h-3 w-3" /> Lead Contattabili
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
